import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faBuildingColumns } from '@fortawesome/pro-regular-svg-icons/faBuildingColumns';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faGarageCar } from '@fortawesome/pro-regular-svg-icons/faGarageCar';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Row, Switch, theme, Typography } from 'antd';
import dayjs from 'dayjs';
import { LocationAssets } from 'pos-client/components/locations/assets/LocationAssets.tsx';
import { LocationManifest } from 'pos-client/components/locations/location-manifest/LocationManifest.tsx';
import { LocationUsers } from 'pos-client/components/locations/users/LocationUsers.tsx';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosColumn } from 'pos-client/components/shared/Layout/Grid/PosColumn.tsx';
import { useLocationModal } from 'pos-client/hooks/useLocationModal.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { PosPageContent } from 'pos-client/layouts/page/PosPageContent.tsx';
import { CreateEditLocationModal } from 'pos-client/pages/locations/Modals/CreateEditLocationModal.tsx';
import { GREEN, LIGHT_GREY } from 'pos-client/utils/colours.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import { WeekdaysEnum } from 'pos-shared/models/commonSchema.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';

import { LocationListItem } from './LocationSummaryListItem.tsx';

export const LocationSummary = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const canAddAndEditLocation = hasPermissions('add.edit.location');
  const { location } = useOutletContext<{
    location: LocationModel;
  }>();
  const {
    isLocationModalOpen,
    showLocationModal,
    hideLocationModal,
    updateLocationStatusHandler,
  } = useLocationModal({ location });

  const { Text } = Typography;

  const { data: openingHoursGroup } = trpc.getOpeningHoursGroupById.useQuery(
    {
      id: location.openingHoursGroupId as string,
    },
    {
      enabled:
        location.openingHoursGroupId !== null &&
        location.openingHoursGroupId !== undefined,
      select: (groups) => {
        const group = groups.results[0];
        return {
          ...group,
          tradingDays: WeekdaysEnum.options.map((v) => {
            const times = group?.tradingTimes.findLast(
              ({ dayOfWeek }) => dayOfWeek.toLowerCase() === v
            );
            return {
              dayOfWeek: t(`common.date.${v}`),
              opening: times?.opening
                ? dayjs(`1/1/1 ${times.opening}`).format('HH:mma')
                : '-',
              closing: times?.closing
                ? dayjs(`1/1/1 ${times.closing}`).format('HH:mma')
                : '-',
            };
          }),
        };
      },
    }
  );

  const { data: locationBusinessOrganisation } =
    trpc.locations.getBusinessOrganisationById.useQuery(
      {
        id: location?.businessOrganisationId || '',
      },
      {
        enabled: !!location?.businessOrganisationId,
        select: (groups) => {
          return groups.slice().shift();
        },
      }
    );

  const address = useMemo(
    () =>
      [
        location.address?.addressLine1,
        location.address?.addressLine2,
        location.address?.addressLine3,
        location.address?.addressLine4,
        location.address?.state,
        location.address?.postalCode,
        location.address?.country,
      ]
        .filter(Boolean)
        .join(', '),
    [location.address]
  );

  const locationDetailsMap: {
    title: string;
    content: ReactNode;
  }[] = [
    {
      title: t('locations.locationType'),
      content: location.serviceTypes.join(', '),
    },
    {
      title: t('locations.locationName'),
      content: location.name ?? '',
    },
    {
      title: t('common.address.address'),
      content: address,
    },
    {
      title: t('common.contact.email'),
      content: location.contact?.emailAddress,
    },
    {
      title: t('common.words.code'),
      content: location.code,
    },
    {
      title: t('locations.locationNumber'),
      content: location.contact?.telephone,
    },
    {
      title: t('common.address.latitude'),
      content: location.address?.geoLocation?.latitude,
    },
    {
      title: t('common.address.longitude'),
      content: location.address?.geoLocation?.longitude,
    },
    {
      title: t('common.time.timezone'),
      content: location.address?.timeZone,
    },
    {
      title: t('locations.category'),
      content: location.linkedCategories?.[0]?.name,
    },
    ...(canAddAndEditLocation
      ? [
          {
            title: t('common.status.status'),
            content: (
              <Switch
                style={{
                  backgroundColor: location.isActive ? GREEN.color : LIGHT_GREY,
                }}
                checkedChildren={
                  <>
                    <FontAwesomeIcon
                      icon={faCheck}
                      color={token.colorWhite}
                      style={{
                        marginRight: token.marginXXS,
                      }}
                    />
                    {t('statuses.active')}
                  </>
                }
                checked={location.isActive === true}
                unCheckedChildren={t('statuses.inactive')}
                aria-label={t('common.status.status')}
                defaultChecked={location.isActive === true}
                onChange={() => {
                  updateLocationStatusHandler(location.isActive);
                }}
              />
            ),
          },
        ]
      : []),
  ].map(({ title, content }) => ({
    title,
    content:
      content === null || content === undefined || content === ''
        ? '-'
        : content,
  }));

  return (
    <PosPageContent row>
      <PosCard
        headerProps={{
          title: t('locations.locationDetails'),
          icon: faGarageCar,
          action: canAddAndEditLocation && (
            <Button
              icon={<FontAwesomeIcon icon={faPenToSquare} />}
              onClick={showLocationModal}
            >
              {t('common.buttons.edit')}
            </Button>
          ),
        }}
        colProps={{
          span: 24,
        }}
      >
        <Row gutter={[token.margin, token.margin]}>
          {locationDetailsMap.map((listItem) => (
            <LocationListItem
              key={listItem.title}
              title={listItem.title}
              content={listItem.content}
            />
          ))}
        </Row>
      </PosCard>
      <PosCard
        headerProps={{
          title: t('locations.locationContact'),
          icon: faUser,
          action: canAddAndEditLocation && (
            <Button
              icon={<FontAwesomeIcon icon={faPenToSquare} />}
              onClick={showLocationModal}
            >
              {t('common.buttons.edit')}
            </Button>
          ),
        }}
        colProps={{
          span: 24,
        }}
      >
        <Row gutter={[token.margin, token.margin]}>
          {location.contact?.emailAddress && (
            <LocationListItem
              title={t('common.contact.emailAddress')}
              content={location.contact?.emailAddress}
            />
          )}
          {location.contact?.telephone && (
            <LocationListItem
              title={t('common.contact.phoneNumber')}
              content={location.contact?.telephone}
            />
          )}
        </Row>
      </PosCard>
      <PosCard
        headerProps={{
          title: t('common.contact.organisation'),
          icon: faBuildingColumns,
        }}
        colProps={{
          span: 24,
        }}
      >
        <Row gutter={[token.margin, token.margin]}>
          <Col>
            <Text strong style={{ color: token.colorTextSecondary }}>
              {t('locations.organisation.hierarchy.group')}
            </Text>
          </Col>
          <Col style={{ color: token.colorTextSecondary }}>
            {locationBusinessOrganisation?.name}
          </Col>
        </Row>
      </PosCard>
      <PosCard
        headerProps={{
          title: t('locations.trading.openingHours'),
          icon: faClock,
          action: canAddAndEditLocation && (
            <Button>
              <Link to={`/locations/${location.id}/hours`}>
                {t('locations.manageHours')}
              </Link>
            </Button>
          ),
        }}
        colProps={{
          span: 24,
        }}
      >
        <Row gutter={[token.margin, token.margin]}>
          <Col style={{ width: '115px' }}>
            <Text strong style={{ color: token.colorTextSecondary }}>
              {t('locations.trading.activeGroup')}
            </Text>
          </Col>
          <Col span={10} style={{ color: token.colorTextSecondary }}>
            {openingHoursGroup?.groupName}
          </Col>
        </Row>
        {openingHoursGroup?.tradingDays.map((openingHours) => {
          return (
            <Row
              key={openingHours.dayOfWeek}
              style={{ marginTop: token.marginSM }}
              gutter={[token.margin, token.margin]}
            >
              <Col style={{ width: '115px' }}>
                <Text strong style={{ color: token.colorTextSecondary }}>
                  {openingHours.dayOfWeek}
                </Text>
              </Col>
              <Col style={{ color: token.colorTextSecondary }}>
                {openingHours.opening} - {openingHours.closing}
              </Col>
            </Row>
          );
        })}
      </PosCard>
      <LocationAssets isLocationOverview />
      <PosColumn span={24}>
        <LocationUsers isLocationOverview />
      </PosColumn>
      {canAddAndEditLocation && <LocationManifest />}
      <CreateEditLocationModal
        isOpen={isLocationModalOpen}
        hideModal={hideLocationModal}
        location={location}
      />
    </PosPageContent>
  );
};
